import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Filter, Download, Plus, Loader2, ArrowRight, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { normalizeStage, stageOrder } from "../appShared";
import {
  STAGE_BADGE,
  CUSTOMER_PHASE_BADGE,
  HEALTH_STATES,
  CUSTOMER_STAGES,
  JOURNEY_STEPS,
  journeyIndexFor,
  estimateValue,
  estimatePaid,
  classifyHealth,
} from "./adminStages";
import { fmtUSDCompact, fmtAddedOn, fmtCustomerSince } from "./adminTheme";
import AddLeadModal from "./AddLeadModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Stage filter pill buckets
const LEADS_FILTERS = [
  { key: "all", label: "All" },
  { key: "new_intake", label: "New Intake", stages: ["NEW_INTAKE"] },
  { key: "assessment", label: "Assessment", stages: ["ASSESSMENT_PURCHASED", "ASSESSMENT_IN_PROGRESS", "ASSESSMENT_DELIVERED"] },
  { key: "scope", label: "Scope", stages: ["SCOPE_CONFIRMED", "SOW_SENT", "SOW_APPROVED"] },
  { key: "in_delivery", label: "In Delivery", stages: ["PROJECT_KICKOFF", "PROJECT_ACTIVE", "PROJECT_IN_DELIVERY"] },
  { key: "needs_action", label: "Needs Action" },
];

const CUSTOMERS_FILTERS = [
  { key: "all", label: "All" },
  { key: "in_delivery", label: "In Delivery", stages: ["PROJECT_KICKOFF", "PROJECT_ACTIVE", "PROJECT_IN_DELIVERY"] },
  { key: "retainer", label: "Retainer", stages: ["RETAINER_OFFERED", "RETAINER_ACTIVE"] },
  { key: "at_risk", label: "At Risk", stages: ["AT_RISK"] },
  { key: "complete", label: "Complete", stages: ["PROJECT_DELIVERED", "PROJECT_COMPLETED", "CHURNED"] },
];

// ───── Detail Panel building blocks ─────

const JourneyDots = ({ stage }) => {
  const activeIdx = journeyIndexFor(stage);
  return (
    <div className="flex items-center gap-1.5">
      {JOURNEY_STEPS.map((s, idx) => {
        const isComplete = idx < activeIdx;
        const isActive = idx === activeIdx;
        return (
          <React.Fragment key={s.key}>
            <div className="flex flex-col items-center gap-1">
              <div
                className="h-7 w-7 rounded-full flex items-center justify-center text-[11.5px] font-bold"
                style={
                  isComplete
                    ? { backgroundColor: "#C79A3B", color: "white" }
                    : isActive
                      ? { backgroundColor: "#0f172a", color: "white", boxShadow: "0 0 0 2px rgba(15,23,42,0.15)" }
                      : { backgroundColor: "#f1f5f9", color: "#94a3b8", border: "1px solid #e2e8f0" }
                }
              >
                {isComplete ? "✓" : idx + 1}
              </div>
              <span className="text-[9px] font-semibold text-[#64748b]">{s.label}</span>
            </div>
            {idx < JOURNEY_STEPS.length - 1 && (
              <div className="h-px w-3 -mt-4" style={{ backgroundColor: idx < activeIdx ? "#C79A3B" : "#e2e8f0" }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const InsightBanner = ({ tone, message }) => {
  const palette = {
    danger: { bg: "#fef2f2", border: "#fecaca", color: "#991b1b" },
    warning: { bg: "#fffbeb", border: "#fde68a", color: "#92400e" },
    info: { bg: "#eff6ff", border: "#bfdbfe", color: "#1e40af" },
    success: { bg: "#f0fdf4", border: "#bbf7d0", color: "#166534" },
    neutral: { bg: "#f8fafc", border: "#e2e8f0", color: "#475569" },
  }[tone] || { bg: "#f8fafc", border: "#e2e8f0", color: "#475569" };

  return (
    <div
      className="p-3 rounded-lg border text-[12.5px] leading-relaxed"
      style={{ backgroundColor: palette.bg, borderColor: palette.border, color: palette.color }}
    >
      {message}
    </div>
  );
};

const stageInsight = (stage, prospect) => {
  const norm = normalizeStage(stage);
  const company = prospect.account?.name || prospect.company_name || "this lead";
  const consultant = prospect.assigned_consultant_name || prospect.assigned_to_user_id;

  switch (norm) {
    case "NEW_INTAKE":
      return { tone: "info", text: `New lead received. Review and qualify ${company} to advance.` };
    case "QUALIFIED":
    case "QUAL_CALL_BOOKED":
    case "QUALIFIED_TO_BUY":
      return { tone: "info", text: "Qualified lead. Send the free preliminary assessment to move forward." };
    case "FREE_ASSESSMENT_COMPLETE":
      return { tone: "warning", text: "Free assessment complete. Send portal access so they can purchase the in-depth assessment." };
    case "ASSESSMENT_PURCHASED":
      return consultant
        ? { tone: "info", text: "Assessment purchased. Begin the in-depth assessment with the assigned consultant." }
        : { tone: "danger", text: "Assessment purchased — consultant assignment pending. Revenue is stalling." };
    case "ASSESSMENT_IN_PROGRESS":
      return { tone: "info", text: "Detailed assessment in progress. Complete and deliver findings." };
    case "ASSESSMENT_DELIVERED":
      return { tone: "warning", text: "Assessment delivered. Awaiting customer to confirm scope in their portal." };
    case "SCOPE_CONFIRMED":
    case "SOW_SENT":
      return { tone: "success", text: "Scope confirmed. Generate the Scope of Services for client approval." };
    case "SOW_APPROVED":
      return { tone: "success", text: "Scope approved. Collect payment for the implementation project to kick off." };
    case "PROJECT_KICKOFF":
    case "PROJECT_ACTIVE":
    case "PROJECT_IN_DELIVERY":
      return { tone: "warning", text: "Active delivery. Confirm weekly check-in is scheduled and milestones are on track." };
    case "PROJECT_DELIVERED":
    case "PROJECT_COMPLETED":
      return { tone: "success", text: "Project complete. Prime candidate for retainer conversion — send an offer." };
    case "RETAINER_ACTIVE":
      return { tone: "success", text: "On retainer. Maintain monthly check-ins and proactively flag risks." };
    case "RETAINER_OFFERED":
      return { tone: "warning", text: "Retainer offered. Follow up to close the recurring engagement." };
    case "AT_RISK":
      return { tone: "danger", text: "Account flagged at risk. Escalate and resolve before churn." };
    case "CHURNED":
      return { tone: "neutral", text: "Account churned. Reopen if circumstances change." };
    case "DISQUALIFIED":
    case "NURTURE":
      return { tone: "neutral", text: "Currently in nurture. Re-qualify when ready." };
    default:
      return { tone: "neutral", text: "Review opportunity and determine next best action." };
  }
};

const SnapshotBox = ({ label, value, valueColor }) => (
  <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-3 text-center">
    <div className="text-[11.5px] font-semibold text-[#4b5563] uppercase tracking-[0.08em]">
      {label}
    </div>
    <div
      className="mt-1.5 text-[15px] font-bold tabular-nums"
      style={{ color: valueColor || "#0f172a" }}
    >
      {value}
    </div>
  </div>
);

// ───── Lead Detail Panel ─────

const LeadDetailPanel = ({ prospect, pricing, onClose, onNavigateWorkspace }) => {
  if (!prospect) return null;
  const stage = normalizeStage(prospect.stage);
  const meta = STAGE_BADGE[stage] || { label: stage, bg: "#f3f4f6", fg: "#475569" };
  const insight = stageInsight(stage, prospect);
  const value = estimateValue(prospect, pricing);
  const paid = estimatePaid(prospect, pricing);
  const interactions = prospect.interactions?.length ?? prospect.interaction_count ?? 0;

  const company = prospect.account?.name || prospect.company_name || "—";
  const industry = prospect.account?.industry || "—";
  const freeAssign = prospect.assigned_consultant_name_initial || prospect.assigned_consultant_name || (prospect.assigned_to_user_id ? "Assigned" : null);
  const detailedAssign = prospect.assigned_consultant_name_detailed || prospect.assigned_consultant_name || (prospect.assigned_to_user_id ? "Assigned" : null);

  const showProjectWorkspaceCTA = stageOrder[stage] >= stageOrder.PROJECT_IN_DELIVERY;

  return (
    <aside
      className="rounded-2xl overflow-hidden border border-[#e5e7eb] bg-white sticky top-6"
      data-testid="lead-detail-panel"
    >
      {/* Dark header */}
      <div className="px-5 pt-5 pb-4 text-white relative" style={{ backgroundColor: "#0B1628" }}>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-[#4b5563] hover:text-white"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="text-[16px] font-bold tracking-[-0.01em]">{company}</div>
        <div className="text-[12px] text-[#4b5563] mt-0.5">{industry}</div>
        <div className="mt-3">
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-full text-[11.5px] font-bold"
            style={{ backgroundColor: "rgba(199, 154, 59, 0.18)", color: "#E5BD63" }}
          >
            {meta.label}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 space-y-5">
        <JourneyDots stage={stage} />

        <InsightBanner tone={insight.tone} message={insight.text} />

        {/* Snapshot */}
        <div>
          <div className="text-[11.5px] font-semibold text-[#4b5563] uppercase tracking-[0.10em] mb-2">
            Snapshot
          </div>
          <div className="grid grid-cols-3 gap-2">
            <SnapshotBox label="Value" value={value ? `${fmtUSDCompact(value)}+` : "—"} />
            <SnapshotBox label="Payments" value={paid > 0 ? fmtUSDCompact(paid) : "—"} valueColor={paid > 0 ? "#16a34a" : undefined} />
            <SnapshotBox label="Activity" value={interactions} />
          </div>
        </div>

        {/* Consultant Assignment */}
        <div>
          <div className="text-[11.5px] font-semibold text-[#4b5563] uppercase tracking-[0.10em] mb-2">
            Consultant Assignment
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-[#e2e8f0] p-2.5">
              <div className="text-[11.5px] text-[#4b5563]">Free Assessment</div>
              <div
                className="text-[12px] font-bold mt-0.5"
                style={{ color: freeAssign ? "#16a34a" : "#dc2626" }}
              >
                {freeAssign || "Unassigned"}
              </div>
            </div>
            <div className="rounded-lg border border-[#e2e8f0] p-2.5">
              <div className="text-[11.5px] text-[#4b5563]">In-Depth Assessment</div>
              <div
                className="text-[12px] font-bold mt-0.5"
                style={{ color: detailedAssign ? "#16a34a" : "#dc2626" }}
              >
                {detailedAssign || "Unassigned"}
              </div>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="space-y-2">
          {showProjectWorkspaceCTA && (
            <button
              type="button"
              onClick={() => onNavigateWorkspace?.(prospect)}
              className="w-full h-10 rounded-lg text-[13px] font-bold text-white shadow-[0_4px_12px_rgba(199,154,59,0.30)] hover:opacity-95 transition-opacity"
              style={{ backgroundColor: "#C79A3B" }}
              data-testid="detail-view-workspace-btn"
            >
              View Project Workspace
            </button>
          )}
          <button
            type="button"
            onClick={() => onNavigateWorkspace?.(prospect)}
            className="w-full h-10 rounded-lg text-[13px] font-semibold text-white hover:opacity-90 transition-opacity"
            style={{ backgroundColor: "#0B1628" }}
            data-testid="detail-book-checkin-btn"
          >
            Book Check-in Call
          </button>
          <button
            type="button"
            onClick={() => onNavigateWorkspace?.(prospect)}
            className="w-full h-10 rounded-lg text-[13px] font-semibold text-[#475569] hover:bg-[#f8fafc] transition-colors border border-[#e2e8f0]"
            data-testid="detail-move-nurture-btn"
          >
            Move to Nurture
          </button>
        </div>

        <button
          type="button"
          onClick={() => onNavigateWorkspace?.(prospect)}
          className="w-full text-[12px] text-[#C79A3B] hover:underline font-semibold flex items-center justify-center gap-1"
        >
          Open full workspace <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </aside>
  );
};

// ───── Customer Detail Panel (richer — shows project health & milestones) ─────

const CustomerDetailPanel = ({ prospect, pricing, onClose, onNavigateWorkspace }) => {
  if (!prospect) return null;
  const stage = normalizeStage(prospect.stage);
  const phase = CUSTOMER_PHASE_BADGE[stage] || { label: stage, bg: "#f3f4f6", fg: "#475569" };
  const insight = stageInsight(stage, prospect);
  const health = classifyHealth(prospect);
  const healthMeta = HEALTH_STATES[health];
  const value = estimateValue(prospect, pricing);
  const paid = estimatePaid(prospect, pricing);
  const company = prospect.account?.name || prospect.company_name || "—";
  const industry = prospect.account?.industry || "—";
  const sinceDate = prospect.created_at;
  const projectHealth = prospect.project_health_percent ?? (health === "on_track" ? 92 : health === "needs_review" ? 72 : health === "at_risk" ? 45 : 100);
  const weeksInDelivery = Math.max(1, Math.floor(((Date.now() - new Date(prospect.updated_at || prospect.created_at).getTime()) / (1000 * 60 * 60 * 24 * 7))));

  // Milestones — derive from project if available, otherwise use generic 4-step
  const milestones = prospect.project_milestones?.length
    ? prospect.project_milestones.slice(0, 4).map((m) => ({
        label: m.label || m.name,
        status: m.status === "complete" || m.status === "live" ? "done" : (m.status === "in_progress" ? "active" : "todo"),
      }))
    : [
        { label: "Workflow Validation", status: stageOrder[stage] >= stageOrder.PROJECT_IN_DELIVERY ? "done" : "todo" },
        { label: "AI Configuration", status: stageOrder[stage] >= stageOrder.PROJECT_IN_DELIVERY ? "done" : "todo" },
        { label: "Testing", status: stageOrder[stage] >= stageOrder.PROJECT_IN_DELIVERY ? "active" : "todo" },
        { label: "Go-Live", status: stageOrder[stage] >= stageOrder.PROJECT_DELIVERED ? "done" : "todo" },
      ];

  return (
    <aside
      className="rounded-2xl overflow-hidden border border-[#e5e7eb] bg-white sticky top-6"
      data-testid="customer-detail-panel"
    >
      {/* Dark header */}
      <div className="px-5 pt-5 pb-4 text-white relative" style={{ backgroundColor: "#0B1628" }}>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-[#4b5563] hover:text-white"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="text-[16px] font-bold tracking-[-0.01em]">{company}</div>
        <div className="text-[12px] text-[#4b5563] mt-0.5">{industry}</div>
        <div className="text-[11.5px] text-[#4b5563] mt-0.5">{fmtCustomerSince(sinceDate)}</div>
        <div className="mt-3">
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-full text-[11.5px] font-bold"
            style={{ backgroundColor: "rgba(199, 154, 59, 0.18)", color: "#E5BD63" }}
          >
            {phase.label}
          </span>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Project Health */}
        <div>
          <div className="flex items-center justify-between text-[11.5px] font-semibold uppercase tracking-[0.10em] mb-2">
            <span className="text-[#4b5563]">Project Health</span>
            <span style={{ color: healthMeta.color }}>{projectHealth}%</span>
          </div>
          <div className="h-2 rounded-full bg-[#f1f5f9] overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: `${projectHealth}%`, backgroundColor: healthMeta.color }}
            />
          </div>
        </div>

        <InsightBanner tone={insight.tone} message={insight.text} />

        {/* Snapshot */}
        <div>
          <div className="text-[11.5px] font-semibold text-[#4b5563] uppercase tracking-[0.10em] mb-2">
            Snapshot
          </div>
          <div className="grid grid-cols-3 gap-2">
            <SnapshotBox label="Contract" value={value ? fmtUSDCompact(value) : "—"} />
            <SnapshotBox label="Paid" value={paid > 0 ? fmtUSDCompact(paid) : "—"} valueColor={paid > 0 ? "#16a34a" : undefined} />
            <SnapshotBox label="Weeks" value={weeksInDelivery} />
          </div>
        </div>

        {/* Delivery Milestones */}
        <div>
          <div className="text-[11.5px] font-semibold text-[#4b5563] uppercase tracking-[0.10em] mb-2">
            Delivery Milestones
          </div>
          <div className="space-y-1.5">
            {milestones.map((m, i) => (
              <div key={i} className="flex items-center gap-2 text-[12.5px]">
                <span
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full shrink-0 text-[9px] font-bold"
                  style={
                    m.status === "done"
                      ? { backgroundColor: "#16a34a", color: "white" }
                      : m.status === "active"
                        ? { backgroundColor: "#C79A3B", color: "white" }
                        : { backgroundColor: "white", color: "#94a3b8", border: "1.5px solid #cbd5e1" }
                  }
                >
                  {m.status === "done" ? "✓" : m.status === "active" ? "↻" : ""}
                </span>
                <span style={{ color: m.status === "todo" ? "#94a3b8" : "#0f172a", fontWeight: m.status === "active" ? 600 : 400 }}>
                  {m.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CTAs */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => onNavigateWorkspace?.(prospect)}
            className="w-full h-10 rounded-lg text-[13px] font-bold text-white shadow-[0_4px_12px_rgba(199,154,59,0.30)] hover:opacity-95 transition-opacity"
            style={{ backgroundColor: "#C79A3B" }}
            data-testid="customer-view-workspace-btn"
          >
            View Workspace
          </button>
          <button
            type="button"
            onClick={() => onNavigateWorkspace?.(prospect)}
            className="w-full h-10 rounded-lg text-[13px] font-semibold text-white hover:opacity-90 transition-opacity"
            style={{ backgroundColor: "#0B1628" }}
            data-testid="customer-book-checkin-btn"
          >
            Book Check-in Call
          </button>
          {(stage === "PROJECT_DELIVERED" || stage === "PROJECT_COMPLETED") && (
            <button
              type="button"
              onClick={() => onNavigateWorkspace?.(prospect)}
              className="w-full h-10 rounded-lg text-[13px] font-semibold text-[#475569] hover:bg-[#f8fafc] transition-colors border border-[#e2e8f0]"
              data-testid="customer-send-retainer-btn"
            >
              Send Retainer Offer
            </button>
          )}
        </div>
      </div>
    </aside>
  );
};

// ───── Main List Screen (Leads or Customers) ─────

export default function PipelineList({ session, mode = "leads" }) {
  const navigate = useNavigate();
  const [allProspects, setAllProspects] = useState([]);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");

  const headers = useMemo(() => ({
    "x-tenant-id": session?.tenant?.id || "tenant_1",
    "x-user-id": session?.user?.id || "admin",
    "x-user-role": session?.user?.role || "admin",
  }), [session]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${BACKEND_URL}/api/prospects`, { headers }).then((r) => r.json()).catch(() => []),
      fetch(`${BACKEND_URL}/api/pricing/public`).then((r) => r.json()).catch(() => null),
    ])
      .then(([list, pr]) => {
        setAllProspects(Array.isArray(list) ? list : []);
        setPricing(pr);
      })
      .catch(() => toast.error("Could not load pipeline"))
      .finally(() => setLoading(false));
  }, [headers]);

  const isCustomers = mode === "customers";
  const filtersConfig = isCustomers ? CUSTOMERS_FILTERS : LEADS_FILTERS;

  // Mode filter (Leads vs Customers)
  const modeFiltered = useMemo(() => {
    return allProspects.filter((p) => {
      const stage = normalizeStage(p.stage);
      const isCust = CUSTOMER_STAGES.has(stage);
      return isCustomers ? isCust : !isCust;
    });
  }, [allProspects, isCustomers]);

  // Stage filter + search
  const filtered = useMemo(() => {
    let list = modeFiltered;
    if (filter !== "all") {
      const f = filtersConfig.find((x) => x.key === filter);
      if (f?.stages) {
        list = list.filter((p) => f.stages.includes(normalizeStage(p.stage)));
      } else if (filter === "needs_action") {
        list = list.filter((p) => {
          const ageDays = Math.floor((Date.now() - new Date(p.updated_at || p.created_at).getTime()) / (1000 * 60 * 60 * 24));
          return !p.assigned_consultant_id && !p.assigned_to_user_id && ageDays >= 7;
        });
      }
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) =>
        (p.account?.name || "").toLowerCase().includes(q) ||
        (p.contact?.full_name || "").toLowerCase().includes(q) ||
        (p.account?.industry || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [modeFiltered, filter, search, filtersConfig]);

  // Auto-select first item
  useEffect(() => {
    if (filtered.length > 0 && !filtered.find((p) => p.id === selectedId)) {
      setSelectedId(filtered[0].id);
    } else if (filtered.length === 0) {
      setSelectedId("");
    }
  }, [filtered, selectedId]);

  const selected = filtered.find((p) => p.id === selectedId) || null;
  const counts = filtersConfig.reduce((acc, f) => {
    if (f.key === "all") acc[f.key] = modeFiltered.length;
    else if (f.stages) acc[f.key] = modeFiltered.filter((p) => f.stages.includes(normalizeStage(p.stage))).length;
    else if (f.key === "needs_action") acc[f.key] = modeFiltered.filter((p) => {
      const ageDays = Math.floor((Date.now() - new Date(p.updated_at || p.created_at).getTime()) / (1000 * 60 * 60 * 24));
      return !p.assigned_consultant_id && !p.assigned_to_user_id && ageDays >= 7;
    }).length;
    return acc;
  }, {});

  // KPI cards top-of-screen
  const kpis = useMemo(() => {
    if (isCustomers) {
      const totalRev = filtered.reduce((sum, p) => sum + (estimatePaid(p, pricing) || 0), 0);
      const onTrack = filtered.filter((p) => classifyHealth(p) === "on_track").length;
      const needsAttn = filtered.filter((p) => ["at_risk", "needs_review"].includes(classifyHealth(p))).length;
      const onRetainer = filtered.filter((p) => normalizeStage(p.stage) === "RETAINER_ACTIVE").length;
      return [
        { label: "Total Revenue Booked", value: `${fmtUSDCompact(totalRev)}+`, sub: `Across ${filtered.length} active customers`, accent: "#0f172a" },
        { label: "Projects On Track", value: onTrack, sub: "Healthy delivery progress", accent: "#16a34a" },
        { label: "Needs Attention", value: needsAttn, sub: "Behind schedule or at risk", accent: "#dc2626" },
        { label: "On Retainer", value: onRetainer, sub: "Recurring monthly engagements", accent: "#C79A3B" },
      ];
    }
    const totalPipeline = filtered.reduce((sum, p) => {
      const v = estimateValue(p, pricing);
      return sum + (v || (pricing?.pricing?.assessment?.amount || 4995));
    }, 0);
    const assessmentPurchased = filtered.filter((p) => normalizeStage(p.stage) === "ASSESSMENT_PURCHASED").length;
    const inDelivery = filtered.filter((p) => ["PROJECT_KICKOFF", "PROJECT_ACTIVE", "PROJECT_IN_DELIVERY"].includes(normalizeStage(p.stage))).length;
    const needsAttn = filtered.filter((p) => {
      const ageDays = Math.floor((Date.now() - new Date(p.updated_at || p.created_at).getTime()) / (1000 * 60 * 60 * 24));
      return !p.assigned_consultant_id && !p.assigned_to_user_id && ageDays >= 7;
    }).length;
    return [
      { label: "Total Pipeline Value", value: `${fmtUSDCompact(totalPipeline)}+`, sub: `Across ${filtered.length} active leads`, accent: "#0f172a" },
      { label: "Assessment Purchased", value: assessmentPurchased, sub: "Ready for consultant assignment", accent: "#16a34a" },
      { label: "Needs Attention", value: needsAttn, sub: "Unassigned or stalled >7 days", accent: "#f59e0b" },
      { label: "In Delivery", value: inDelivery, sub: "Active projects underway", accent: "#0f172a" },
    ];
  }, [filtered, pricing, isCustomers]);

  // CSV export
  const handleExport = () => {
    if (!filtered.length) {
      toast.message("Nothing to export");
      return;
    }
    const rows = [
      ["Company", "Industry", "Stage", "Contact", "Email", "Created"],
      ...filtered.map((p) => [
        p.account?.name || "",
        p.account?.industry || "",
        normalizeStage(p.stage),
        p.contact?.full_name || "",
        p.contact?.email || "",
        p.created_at || "",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${isCustomers ? "customers" : "leads"}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleNavigateWorkspace = (prospect) => {
    // Always route through the new clean Admin Workspace screen
    navigate(`/app/${isCustomers ? "customers" : "pipeline"}/${prospect.id}`);
  };

  // ───── Render ─────
  return (
    <div className="space-y-6" data-testid={isCustomers ? "customers-screen" : "leads-screen"}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-[13px] text-[#64748b]">
        <span>Overview</span>
        <span className="text-[#cbd5e1]">/</span>
        <span className="font-semibold text-[#0f172a]">{isCustomers ? "Customers" : "Leads"}</span>
      </div>

      {/* Page header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <h1 className="text-[28px] font-bold tracking-[-0.02em] text-[#0f172a]">
            {isCustomers ? "Customers" : "Leads"}
          </h1>
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-full text-[11.5px] font-semibold"
            style={{ backgroundColor: "#eff6ff", color: "#1e40af" }}
          >
            {modeFiltered.length} active
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 h-10 px-3 rounded-lg border border-[#e5e7eb] bg-white w-[260px]">
            <Search className="h-4 w-4 text-[#4b5563] shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isCustomers ? "Search customers..." : "Search leads..."}
              className="flex-1 bg-transparent text-[13px] focus:outline-none"
              data-testid="pipeline-search-input"
            />
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-[#e5e7eb] bg-white text-[13px] font-medium text-[#475569] hover:bg-[#f8fafc]"
          >
            <Filter className="h-4 w-4" /> Filter
          </button>
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-[#e5e7eb] bg-white text-[13px] font-medium text-[#475569] hover:bg-[#f8fafc]"
            data-testid="pipeline-export-btn"
          >
            <Download className="h-4 w-4" /> Export
          </button>
          <button
            type="button"
            onClick={() => setAddLeadOpen(true)}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-lg text-[13px] font-semibold text-white shadow-[0_4px_12px_rgba(199,154,59,0.35)] hover:opacity-95"
            style={{ backgroundColor: "#C79A3B" }}
            data-testid="pipeline-add-btn"
          >
            <Plus className="h-4 w-4" /> {isCustomers ? "Add Customer" : "Add Lead"}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-xl bg-white border border-[#e5e7eb] p-5">
            <div className="text-[11.5px] font-semibold tracking-[0.14em] text-[#4b5563] uppercase">
              {k.label}
            </div>
            <div
              className="mt-2 text-[34px] font-bold tracking-[-0.02em] leading-none"
              style={{ color: k.accent }}
            >
              {k.value}
            </div>
            <div className="mt-3 text-[12px] text-[#64748b]">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Two-column: list + detail */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-5">
        {/* List */}
        <div className="rounded-2xl border border-[#e5e7eb] bg-white">
          {/* Title + filter pills */}
          <div className="flex items-center justify-between gap-4 px-5 pt-5 pb-4 border-b border-[#e5e7eb] flex-wrap">
            <h2 className="text-[15px] font-bold text-[#0f172a]">
              {isCustomers ? "All Customers" : "All Leads"}
            </h2>
            <div className="flex flex-wrap gap-1.5">
              {filtersConfig.map((f) => {
                const active = filter === f.key;
                const c = counts[f.key] ?? 0;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all border"
                    style={
                      active
                        ? { backgroundColor: "#0B1628", color: "white", borderColor: "#0B1628" }
                        : { backgroundColor: "white", color: "#475569", borderColor: "#e5e7eb" }
                    }
                    data-testid={`filter-${f.key}`}
                  >
                    {f.label}
                    {c > 0 && <span className={active ? "text-white/70" : "text-[#4b5563]"}>·{c}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Header row */}
          <div className={`grid ${isCustomers ? "grid-cols-[1fr_120px_140px_120px_120px_140px]" : "grid-cols-[1fr_120px_180px_120px_140px]"} gap-3 px-5 py-3 border-b border-[#e5e7eb] text-[11.5px] font-bold tracking-[0.10em] uppercase text-[#4b5563]`}>
            <span>Company</span>
            <span>Industry</span>
            <span>{isCustomers ? "Phase" : "Stage"}</span>
            {isCustomers && <span>Health</span>}
            <span>Value</span>
            <span>Consultant</span>
          </div>

          {/* Rows */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-[#4b5563]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-[13px] text-[#4b5563]">
              No {isCustomers ? "customers" : "leads"} match this filter.
            </div>
          ) : (
            <div className="divide-y divide-[#f1f5f9]">
              {filtered.map((p) => {
                const stage = normalizeStage(p.stage);
                const meta = (isCustomers ? CUSTOMER_PHASE_BADGE : STAGE_BADGE)[stage] || { label: stage, bg: "#f3f4f6", fg: "#475569" };
                const value = estimateValue(p, pricing);
                const isSelected = selectedId === p.id;
                const consultant = p.assigned_consultant_name || (p.assigned_to_user_id ? "Assigned" : null);
                const health = isCustomers ? classifyHealth(p) : null;
                const healthMeta = health ? HEALTH_STATES[health] : null;
                const since = isCustomers ? fmtCustomerSince(p.created_at) : fmtAddedOn(p.created_at);

                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedId(p.id)}
                    className={`w-full grid ${isCustomers ? "grid-cols-[1fr_120px_140px_120px_120px_140px]" : "grid-cols-[1fr_120px_180px_120px_140px]"} gap-3 px-5 py-4 text-left items-center transition-colors`}
                    style={isSelected ? { backgroundColor: "#fffbeb" } : {}}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = "#f8fafc"; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = "transparent"; }}
                    data-testid={`pipeline-row-${p.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-[13px] font-bold text-[#0f172a] truncate">
                        {p.account?.name || "—"}
                      </div>
                      <div className="text-[11.5px] text-[#4b5563] mt-0.5 truncate">{since}</div>
                    </div>
                    <div className="text-[12.5px] text-[#475569] capitalize truncate">
                      {p.account?.industry || "—"}
                    </div>
                    <div>
                      <span
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-[11.5px] font-bold whitespace-nowrap"
                        style={{ backgroundColor: meta.bg, color: meta.fg }}
                      >
                        {meta.label}
                      </span>
                    </div>
                    {isCustomers && (
                      <div className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: healthMeta.dot }} />
                        <span className="text-[12.5px] font-semibold" style={{ color: healthMeta.color }}>
                          {healthMeta.label}
                        </span>
                      </div>
                    )}
                    <div className="text-[13px] font-bold text-[#0f172a] tabular-nums">
                      {value ? `${fmtUSDCompact(value)}` : "—"}
                      {isCustomers && normalizeStage(p.stage) === "RETAINER_ACTIVE" && <span className="text-[11.5px] font-normal text-[#4b5563]">/mo</span>}
                    </div>
                    <div className={`text-[12.5px] font-semibold truncate ${consultant ? "text-[#16a34a]" : "text-[#dc2626]"}`}>
                      {consultant || "Unassigned"}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div>
          {selected ? (
            isCustomers ? (
              <CustomerDetailPanel
                prospect={selected}
                pricing={pricing}
                onNavigateWorkspace={handleNavigateWorkspace}
              />
            ) : (
              <LeadDetailPanel
                prospect={selected}
                pricing={pricing}
                onNavigateWorkspace={handleNavigateWorkspace}
              />
            )
          ) : (
            <div className="rounded-2xl border border-[#e5e7eb] bg-white p-8 text-center text-[13px] text-[#4b5563]">
              Select a {isCustomers ? "customer" : "lead"} to see details
            </div>
          )}
        </div>
      </div>
      <AddLeadModal
        open={addLeadOpen}
        onClose={() => setAddLeadOpen(false)}
        onCreated={(p) => { if (p?.id) navigate(`/app/leads/${p.id}`); }}
      />
    </div>
  );
}
